import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, GetGuestsByEventId, GetCardTypes } from '../../api/api';
import { isMarriageEventType } from '../../utils/helpers';
import { functionHeadcount } from '../../utils/headcount';

// Shared logic for the venue headcount: persons per function, parties deduped
// by mobile (a family invited by several members counts once), split by Side,
// plus the unnamed guests on extra cards for the Reception-card functions
// (utils/headcount.js holds the rule).
export function useHeadcount(event) {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const openRow = (fn) => nav('/app/members?fn=' + fn.FunctionId); // drill in to that function's guests
  const toast = useToast();
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      GetFunctionsByEventId({ EventId: event.EventId }),
      GetGuestsByEventId({ EventId: event.EventId }),
      GetCardTypes({ EventId: event.EventId }).catch(() => []), // table not migrated yet → no extras
    ]).then(([f, g, t]) => {
      setFunctions(Array.isArray(f) ? f : []);
      setRows(Array.isArray(g) ? g : []);
      setCardTypes(Array.isArray(t) ? t : []);
    }).catch(() => toast('Failed to load')).finally(() => setLoading(false));
  }, [event, toast]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const perFunction = useMemo(
    () => functions.map(fn => ({ fn, ...functionHeadcount(rows, fn.FunctionId, cardTypes) })),
    [functions, rows, cardTypes]
  );

  const grand = useMemo(
    () => perFunction.reduce((a, r) => ({ parties: a.parties + r.parties, persons: a.persons + r.persons }), { parties: 0, persons: 0 }),
    [perFunction]
  );

  return { isAdmin, loading, perFunction, grand, isTwoSided, openRow };
}
